import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { cn } from './cn.js';

/// Escolha única entre opções fixas, com um indicador compartilhado que acompanha a seleção.
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  className,
  tabs = false,
  compact = false,
}: {
  label: string;
  options: ReadonlyArray<{ value: T; label: ReactNode; accessibleLabel?: string }>;
  value: T;
  onChange: (value: T) => void;
  className?: string | undefined;
  tabs?: boolean;
  /// Opções curtas permanecem juntas, sem crescer nem quebrar em linhas.
  compact?: boolean;
}) {
  const root = useRef<HTMLDivElement>(null);
  const indicator = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const container = root.current!;
    const update = () => {
      const selected = container.querySelector<HTMLButtonElement>('[data-selected="true"]');
      const highlight = indicator.current!;
      highlight.style.opacity = selected ? '1' : '0';
      if (!selected) return;
      highlight.style.width = `${selected.offsetWidth}px`;
      highlight.style.height = `${selected.offsetHeight}px`;
      highlight.style.transform = `translate(${selected.offsetLeft}px, ${selected.offsetTop}px)`;
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(container);
    for (const button of container.querySelectorAll('button')) observer.observe(button);
    return () => observer.disconnect();
  }, [value, options]);
  return (
    <div
      ref={root}
      role={tabs ? 'tablist' : 'group'}
      aria-label={label}
      className={cn(
        // A mesma altura de um campo de texto em qualquer densidade, para alinhar ao lado dele.
        'relative gap-0.5',
        tabs
          ? 'border-b border-surface-border'
          : 'hue-segmented rounded-xl border border-surface-border bg-surface-elevated p-0.5',
        compact ? 'inline-flex shrink-0 flex-nowrap' : 'flex flex-wrap',
        className,
      )}
    >
      <span
        ref={indicator}
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute left-0 top-0 transition-[transform,width,height,opacity] duration-200 ease-out motion-reduce:transition-none',
          tabs ? 'border-b-2 border-brand-400' : 'rounded-lg bg-brand-500 shadow-sm',
        )}
      />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role={tabs ? 'tab' : undefined}
          aria-selected={tabs ? option.value === value : undefined}
          aria-pressed={tabs ? undefined : option.value === value}
          aria-label={option.accessibleLabel}
          data-selected={option.value === value}
          onClick={() => onChange(option.value)}
          className={cn(
            'relative z-10 cursor-pointer font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 motion-reduce:transition-none',
            'min-h-[2.625rem] px-3 text-sm',
            !tabs && 'rounded-lg',
            compact ? 'shrink-0 whitespace-nowrap' : 'min-w-0 flex-1 basis-24',
            option.value === value
              ? tabs
                ? 'text-brand-400'
                : 'text-on-brand'
              : 'text-content-muted hover:text-content',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
